/**
 * <newsletter-subscribe> Custom Element
 *
 * Replaces theme/js/components/footer.js. Singleton: the snippet markup is
 * captured into app.get('dom.newsletter.signup') (already a parsed node via
 * app.parseHtml) and cloned into <body> on demand. Document-delegated
 * handlers for `.js-newsletter-trigger` and `[data-newsletter-subscribe]`
 * attach once at module evaluation.
 *
 * Phase 13: native fetch + URLSearchParams (no jQuery / serializeJSON).
 *
 * AI: Entirely written by AI
 * Date: 2026-05-01
 * Model: Claude Opus 4.7
 * Updated: 2026-05-04 (Claude Opus 4.7) - Centralize easing/safeAppCall via core helpers
 */

import { log } from './log.js';
import { fadeOut } from './core-anim.js';
import { appGet, safeAppCall } from './app-get.js';
import { getEasing } from './easing.js';
import { push as notifierPush } from './notifier.js';
import { saveLastFocused, restoreLastFocused, bindEscape, unbindEscape } from './overlay.js';
import { fetchSection } from './sections.js';

const TRIGGER_SELECTOR = '.js-newsletter-trigger, .js-newsletter-close';
const FORM_SELECTOR = '[data-newsletter-subscribe]';
const SIGNUP_SELECTOR = '#newsletter-signup';
const DURATION = 400;

function findInstance() {
  return document.querySelector(SIGNUP_SELECTOR);
}

class NewsletterSubscribe extends HTMLElement {
  connectedCallback() {
    const focusSelector =
      this.dataset.focus === 'input' ? 'input[type="email"]' : '.js-newsletter-close';

    this.style.display = 'block';
    this.animate(
      [
        { transform: 'translateY(150%)', opacity: 0 },
        { transform: 'translateY(0)', opacity: 1 },
      ],
      { duration: DURATION, easing: getEasing(), fill: 'forwards' }
    ).finished.then(
      () => {
        // 50ms defer matches the original — focus + trap + Esc binding need
        // to land after the animation settles to avoid focus thrash.
        setTimeout(() => {
          if (!this.isConnected) return;
          const target = this.querySelector(focusSelector);
          if (target) target.focus();
          safeAppCall('trapFocus', this);
          bindEscape(this, () => closeNewsletter());
        }, 50);
      },
      () => {} // animation cancelled by removal mid-flight; nothing to do
    );
  }
}

// Promise-cache the Section Rendering API response. The signup section is
// global (any URL works), so we always fetch from `/` and key the cache
// by the section id. Storing the in-flight promise (not the resolved
// string) coalesces concurrent open calls during the network round-trip.
let signupHtmlPromise = null;

function loadSignupHtml() {
  if (signupHtmlPromise) return signupHtmlPromise;
  signupHtmlPromise = fetchSection('newsletter-signup-modal', '/').catch((err) => {
    signupHtmlPromise = null;
    throw err;
  });
  return signupHtmlPromise;
}

// opts.focus: 'close' (default) or 'input'. Auto-triggered opens use the
// default so stray keystrokes don't land in the email field.
// opts.source: 'footer-button' or 'auto-popup' (default) — GA event label.
async function openNewsletter(opts) {
  if (findInstance()) return;
  opts = opts || {};
  const source = opts.source || 'auto-popup';
  // Auto-popup defers when a notifier modal is already open: stacking two
  // role="dialog" aria-modal="true" elements confuses AT about which is the
  // active modal, and the two trapFocus regions compete. Manual triggers
  // (footer button) still win — the user expressly asked for the newsletter.
  if (source === 'auto-popup' && document.querySelector('.js-modal-popup')) return;
  saveLastFocused();

  let html;
  try {
    html = await loadSignupHtml();
  } catch (err) {
    log('[Newsletter] fetch failed', err);
    restoreLastFocused();
    return;
  }
  // Settings can suppress the snippet (no mailing list configured), in
  // which case the section response is an empty string — bail out
  // without inserting an empty container.
  if (!html || !html.trim()) {
    restoreLastFocused();
    return;
  }
  // A user may have clicked again during the fetch (cached or not). Re-
  // check before inserting to avoid two stacked instances.
  if (findInstance()) return;

  const doc = new DOMParser().parseFromString(html, 'text/html');
  const clone = doc.querySelector(SIGNUP_SELECTOR);
  if (!clone) {
    log('[Newsletter] no #newsletter-signup in section response');
    restoreLastFocused();
    return;
  }
  clone.style.display = 'none';
  if (opts.focus) clone.dataset.focus = opts.focus;
  document.body.appendChild(clone);

  // Fire the GA Impression AFTER the modal lands in the DOM — not at the
  // top of the function. Pre-fetch firing over-counted impressions on
  // every bail-out path (fetch error, empty section response, concurrent
  // click that lost the findInstance race), inflating auto-popup
  // conversion denominators relative to actual visible exposures.
  safeAppCall('ga', 'send', 'event', {
    eventCategory: 'Newsletter',
    eventAction: 'Impression',
    eventLabel: source,
  });
}

function closeNewsletter(callback) {
  const el = findInstance();
  if (!el) return;
  unbindEscape(el);
  fadeOut(el, { duration: DURATION }).then(() => {
    el.remove();
    restoreLastFocused();
    if (typeof callback === 'function') callback();
  });
}

function closeAndNotify(message) {
  closeNewsletter(() => {
    notifierPush(message);
  });
}

function handleTriggerClick() {
  if (findInstance()) {
    closeNewsletter();
  } else {
    openNewsletter({ focus: 'input', source: 'footer-button' });
  }
}

function handleSubmit(event) {
  event.preventDefault();
  const form = event.target;
  if (!(form instanceof HTMLFormElement)) return;
  const url = form.getAttribute('action').replace('subscriptions', 'ajax/subscriptions');
  const emailEl = form.querySelector('input[type="email"]');
  const email = emailEl ? emailEl.value : '';
  const submit = form.querySelector('button[type="submit"]');
  if (submit) submit.disabled = true;

  // application/x-www-form-urlencoded body — same wire format the IIFE
  // produced via jQuery.serializeJSON, derived from native FormData.
  const body = new URLSearchParams(new FormData(form)).toString();
  fetch(url, {
    method: 'POST',
    mode: 'cors',
    headers: {
      'content-type': 'application/x-www-form-urlencoded',
      'cache-control': 'no-cache',
    },
    body,
  })
    .then((res) => res.json().then((data) => ({ ok: res.ok, data })))
    .then(({ ok, data }) => {
      log(data);
      if (ok && (data.success === true || (data.data && data.data.is_subscribed === true))) {
        onSubscribeSuccess(email);
      } else {
        closeAndNotify(data && data.errors);
      }
    })
    .catch((err) => {
      const msg = 'Email signup error: ' + (err && err.message ? err.message : err);
      closeAndNotify(msg);
      log(msg);
    });
}

function onSubscribeSuccess(email) {
  const hasEmail = typeof email === 'string' && email.length > 0;
  safeAppCall('ga', 'send', 'event', {
    eventCategory: 'Newsletter',
    eventAction: 'Submitted',
    eventLabel: 'Subscribed',
  });
  try {
    if (hasEmail) window.localStorage.setItem('ep-newsletter-email', email);
    window.localStorage.setItem('ep-newsletter-subscribed', '1');
  } catch (e) {}
  if (hasEmail) safeAppCall('set', 'newsletter.subscribedEmail', email);
  closeAndNotify(appGet('notices.newsletter.subscribed'));
}

document.addEventListener('click', (event) => {
  const trigger = event.target.closest && event.target.closest(TRIGGER_SELECTOR);
  if (trigger) handleTriggerClick();
});
document.addEventListener('submit', (event) => {
  if (event.target && event.target.matches && event.target.matches(FORM_SELECTOR)) {
    handleSubmit(event);
  }
});

customElements.define('newsletter-subscribe', NewsletterSubscribe);
log('[NewsletterSubscribe] registered');

export { NewsletterSubscribe, openNewsletter, closeNewsletter };
